import { useIntersect } from "@/hooks/useIntersect";
import { useObserver } from "@/hooks/useObserver";
import { cn } from "@/libs/utils";
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

// 상단에 도달했는지 확인. offset 있다면 상단에서 offset 만큼 떨어진 지점에 도달했는지 확인
const useIntersectTop = (
  ref: React.RefObject<HTMLDivElement | null>,
  offset = 0,
) => {
  const [isTop, setIsTop] = useState(false);
  const onIntersect = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      setIsTop(entry.boundingClientRect.top <= offset && !entry.isIntersecting);
    },
    [offset],
  );

  useIntersect(ref, onIntersect, {
    rootMargin: `-${Math.abs(offset)}px 0px 0px 0px`,
    threshold: 1,
  });
  return isTop;
};

const useBound = (ref: React.RefObject<HTMLElement | null>, ...deps: any) => {
  const [bound, setBound] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const onObserve = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setBound({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [...deps]);
  useObserver(ref, onObserve);
  return bound;
};

/* 
상단에서 얼마나 떨어진 지점에 도달했는지 확인후 sticky로 만들어주는 컴포넌트입니다.
offset은 상단에서 얼마나 떨어진 지점에 도달했는지 확인할 지점을 지정해줍니다.
sticky가 되면 portal 을 이용해서 body에 붙여줍니다.
*/
export default function Sticky({
  children,
  offset = 0,
  className = "",
  cloneable = true,
}: {
  children: React.ReactNode;
  offset?: number; // 상단에서 얼마나 떨어진 지점에 도달했는지 확인
  className?: string;
  cloneable?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isSticky = useIntersectTop(ref, offset);
  const bound = useBound(ref, offset, isSticky);

  return (
    <>
      <div
        ref={ref}
        className={className}
        style={{ opacity: isSticky ? 0 : 1 }}
      >
        {children}
      </div>
      {isSticky &&
        cloneable &&
        createPortal(
          <div
            className={cn("fixed bg-white")}
            style={{
              top: offset,
              left: bound.left,
              width: bound.width,
              height: bound.height,
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
